<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

const props = defineProps<{ text: string; label?: string; ariaName?: string; compact?: boolean; focus?: string }>()

// With `focus`, the part of the prompt unique to this recipe is bright and the
// shared rest is muted. Copy still copies the whole text.
const parts = computed(() => {
  const i = props.focus ? props.text.indexOf(props.focus) : -1
  if (!props.focus || i < 0) return null
  return { head: props.text.slice(0, i), mid: props.focus, tail: props.text.slice(i + props.focus.length) }
})

// `navigator` is touched only inside the click handler so prerender never reaches it.
const copied = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined
async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    return
  }
  copied.value = true
  clearTimeout(timer)
  timer = setTimeout(() => { copied.value = false }, 1500)
}
onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
  <div>
    <div class="flex items-center gap-3" :class="[label ? 'justify-between mb-1.5' : 'justify-end mb-2']">
      <span v-if="label" class="text-xs font-medium text-surface-400">{{ label }}</span>
      <button
        type="button"
        :aria-label="`Copy ${ariaName ?? label ?? 'prompt'}`"
        class="shrink-0 min-h-11 px-4 py-2 text-xs font-medium rounded-full border border-surface-700 text-surface-300 hover:text-primary-300 hover:border-primary-300 transition-colors"
        @click="copy(text)"
      >{{ copied ? 'Copied' : 'Copy' }}</button>
    </div>
    <span class="sr-only" aria-live="polite">{{ copied ? 'Copied' : '' }}</span>
    <pre
      class="whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-card border border-surface-800 bg-surface-800 text-sm leading-relaxed font-mono"
      :class="[compact ? 'p-3' : 'p-4', parts ? 'text-surface-400' : 'text-surface-100']"
    ><template v-if="parts">{{ parts.head }}<span class="text-surface-50 font-semibold">{{ parts.mid }}</span>{{ parts.tail }}</template><template v-else>{{ text }}</template></pre>
  </div>
</template>
